import React, { useState } from "react";
import axios from "axios";
import { UploadZone } from "../components/UploadZone";
import { CVPreview } from "../components/CVPreview";
import { CoverLetterPreview } from "../components/CoverLetterPreview";
import { KeywordMatch } from "../components/KeywordMatch";
import { Rationale } from "../components/Rationale";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Sparkles, Download, Zap, FileText, Mail } from "lucide-react";
import { toast } from "sonner";
import { getDeviceId } from "../lib/device";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEXT = {
  fr: {
    eyebrow: "ATS-Master Optimizer",
    badge: "Claude Sonnet 4.5",
    h1a: "Un CV qui passe",
    h1b: "les filtres IA.",
    lede: "Transformez votre CV — ou rédigez une lettre de motivation Vous-Moi-Nous — en documents haute-performance pour le screening ATS.",
    freeBadge: "100% gratuit · sans compte",
    modeCv: "CV",
    modeCl: "Lettre de motivation",
    modeCvHint: "Réécriture du CV alignée à l'offre",
    modeClHint: "Lettre Vous-Moi-Nous, 300-400 mots",
    stepUpload: "01 — Téléversez votre CV",
    stepUploadCL: "01 — Téléversez votre CV (source)",
    stepJD: "02 — Collez la description du poste",
    stepConfig: "03 — Paramètres",
    lang: "Langue de sortie",
    format: "Format du fichier",
    langFr: "Français",
    langEn: "Anglais",
    analyze: "Analyser & Optimiser",
    analyzeCl: "Rédiger la lettre",
    analyzing: "Analyse en cours…",
    analyzingCl: "Rédaction en cours…",
    placeholderJD: "Collez ici l'offre d'emploi : intitulé, missions, hard skills requis, soft skills, niveau d'expérience…",
    preview: "Aperçu A4",
    keywords: "Mots-clés",
    analyse: "Analyse",
    structure: "Structure",
    download: "Télécharger",
    targetTitle: "Poste visé",
    footer: "Optimisé pour passer les screenings ATS — rédigé par Claude Sonnet 4.5.",
    errNoFile: "Veuillez téléverser un CV (PDF ou DOCX).",
    errNoJD: "La description du poste est trop courte.",
    errGeneric: "Une erreur est survenue.",
    successDoneCv: "CV optimisé avec succès.",
    successDoneCl: "Lettre de motivation générée.",
    wordCount: "Mots",
    integratedKw: "Compétences clés intégrées",
    rateLimit: "Trop de générations. Réessayez plus tard.",
  },
  en: {
    eyebrow: "ATS-Master Optimizer",
    badge: "Claude Sonnet 4.5",
    h1a: "A resume that beats",
    h1b: "the bots.",
    lede: "Optimize your resume — or draft a You-Me-Us cover letter — into ATS-ready, high-performance documents.",
    freeBadge: "100% free · no account",
    modeCv: "Resume",
    modeCl: "Cover Letter",
    modeCvHint: "JD-aligned resume rewrite",
    modeClHint: "You-Me-Us letter, 300-400 words",
    stepUpload: "01 — Upload your CV",
    stepUploadCL: "01 — Upload your CV (source)",
    stepJD: "02 — Paste the job description",
    stepConfig: "03 — Settings",
    lang: "Output language",
    format: "File format",
    langFr: "French",
    langEn: "English",
    analyze: "Analyze & Optimize",
    analyzeCl: "Draft the letter",
    analyzing: "Analyzing…",
    analyzingCl: "Drafting…",
    placeholderJD: "Paste the job offer here: title, responsibilities, required hard skills, soft skills, seniority…",
    preview: "A4 Preview",
    keywords: "Keywords",
    analyse: "Analysis",
    structure: "Structure",
    download: "Download",
    targetTitle: "Target role",
    footer: "Optimized to pass ATS screenings — powered by Claude Sonnet 4.5.",
    errNoFile: "Please upload a CV (PDF or DOCX).",
    errNoJD: "The job description is too short.",
    errGeneric: "Something went wrong.",
    successDoneCv: "Resume optimized successfully.",
    successDoneCl: "Cover letter generated.",
    wordCount: "Words",
    integratedKw: "Integrated key skills",
    rateLimit: "Too many generations. Please try again later.",
  },
};

export default function Optimizer() {
  const [uiLang, setUiLang] = useState("fr");
  const [mode, setMode] = useState("cv"); // 'cv' | 'cover_letter'
  const [file, setFile] = useState(null);
  const [jd, setJD] = useState("");
  const [outLang, setOutLang] = useState("fr");
  const [format, setFormat] = useState("pdf");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const t = TEXT[uiLang];

  const onOptimize = async () => {
    if (!file) { toast.error(t.errNoFile); return; }
    if (!jd || jd.trim().length < 20) { toast.error(t.errNoJD); return; }

    const form = new FormData();
    form.append("cv_file", file);
    form.append("job_description", jd);
    form.append("language", outLang);
    form.append("output_format", format);
    form.append("mode", mode);
    form.append("device_id", getDeviceId());

    setLoading(true);
    setResult(null);
    try {
      // 1. Kick off the job — returns quickly with a job_id + status: pending
      const { data: start } = await axios.post(`${API}/optimize`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 25000,
      });
      const jobId = start.job_id;

      // 2. Poll until completed / failed (max ~2 min)
      const started = Date.now();
      const maxMs = 120000;
      const pollOnce = async () => {
        const { data } = await axios.get(`${API}/optimize/${jobId}`, { timeout: 15000 });
        return data;
      };
      let statusDoc = null;
      while (Date.now() - started < maxMs) {
        await new Promise((r) => setTimeout(r, 1800));
        statusDoc = await pollOnce();
        if (statusDoc.status === "completed" || statusDoc.status === "failed") break;
      }
      if (!statusDoc || statusDoc.status !== "completed") {
        throw new Error(statusDoc?.error || t.errGeneric);
      }
      setResult({
        job_id: jobId,
        filename: statusDoc.filename,
        optimized: statusDoc.optimized,
        output_format: statusDoc.output_format,
        language: statusDoc.language,
        mode: statusDoc.mode || mode,
      });
      toast.success(mode === "cover_letter" ? t.successDoneCl : t.successDoneCv);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429) {
        const msg = (typeof detail === "object" && detail?.message) || t.rateLimit;
        toast.error(msg);
        return;
      }
      const msg = (typeof detail === "string" ? detail : detail?.message) || e?.message || t.errGeneric;
      toast.error(typeof msg === "string" ? msg : t.errGeneric);
    } finally {
      setLoading(false);
    }
  };

  // Direct download via anchor: the backend sets `Content-Disposition: attachment`
  // and the browser handles it natively (no popup blocker, no JS blob hassle).
  const downloadUrl = result?.job_id ? `${API}/download/${result.job_id}` : null;

  return (
    <div className="min-h-screen relative text-[var(--ink)]">
      <div className="bg-mesh" />
      <div className="bg-grid" />

      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[rgba(8,9,12,0.75)] border-b border-[var(--border)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="relative h-[88px] w-[88px] overflow-hidden flex items-center justify-center -my-4 shrink-0">
              <img
                src="https://customer-assets.emergentagent.com/job_keyword-matcher-10/artifacts/yb0ij9lz_Logo%20SmartResume.png"
                alt="SmartResume logo"
                className="h-full w-full object-contain select-none"
                style={{ transform: "scale(1.9)" }}
                draggable={false}
                data-testid="brand-logo"
              />
            </div>
            <div className="hidden lg:flex flex-col leading-tight min-w-0">
              <span className="wordmark" data-testid="brand-wordmark">
                Smart<span className="wm-accent">Resume</span><span className="wm-dot" />
              </span>
              <span className="text-[10px] text-[var(--ink-dim)] font-mono mt-0.5 tracking-wider">v1.0</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div
              data-testid="free-badge"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--accent)]/40 bg-[var(--accent)]/10 text-[11px] font-mono tracking-wide text-[var(--ink)]"
            >
              <Sparkles size={12} className="text-[var(--accent)]" />
              <span>{t.freeBadge}</span>
            </div>
            <div className="flex items-center gap-1 border border-[var(--border)] rounded-full p-1 bg-[var(--surface)]">
              <button
                onClick={() => setUiLang("fr")}
                className={`text-[11px] px-3 py-1 rounded-full transition-colors ${uiLang === "fr" ? "bg-[var(--ink)] text-black" : "text-[var(--ink-dim)] hover:text-[var(--ink)]"}`}
                data-testid="ui-lang-fr"
              >FR</button>
              <button
                onClick={() => setUiLang("en")}
                className={`text-[11px] px-3 py-1 rounded-full transition-colors ${uiLang === "en" ? "bg-[var(--ink)] text-black" : "text-[var(--ink-dim)] hover:text-[var(--ink)]"}`}
                data-testid="ui-lang-en"
              >EN</button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-14 pb-6 relative z-10">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[var(--border-bright)] bg-[var(--surface)] text-[11px] font-mono text-[var(--ink-dim)] mb-6">
            <Zap size={12} className="text-[var(--accent)]" />
            <span>{t.badge}</span>
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tight leading-[1.02] text-[var(--ink)]">
            {t.h1a}{" "}
            <span className="italic text-transparent bg-clip-text" style={{ backgroundImage: "linear-gradient(90deg, #7dd3fc, #a78bfa)" }}>
              {t.h1b}
            </span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-[var(--ink-dim)] max-w-2xl leading-relaxed">
            {t.lede}
          </p>
        </div>
      </section>

      {/* Mode toggle — central, prominent, top of the workspace */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-8 relative z-10">
        <div className="flex justify-center">
          <div
            className="grid grid-cols-2 gap-2 p-1.5 rounded-full border border-[var(--border-bright)] bg-[var(--surface)] w-full max-w-2xl"
            role="tablist"
            data-testid="mode-toggle"
          >
            <button
              onClick={() => { if (mode !== "cv") { setMode("cv"); setResult(null); } }}
              disabled={loading}
              data-testid="mode-cv"
              role="tab"
              aria-selected={mode === "cv"}
              className={`group flex items-center justify-center gap-3 px-5 py-3 rounded-full transition-all ${
                mode === "cv"
                  ? "bg-[var(--ink)] text-black shadow-[0_0_0_1px_rgba(255,255,255,0.04)]"
                  : "text-[var(--ink-dim)] hover:text-[var(--ink)]"
              }`}
            >
              <FileText size={16} className={mode === "cv" ? "text-black" : ""} />
              <div className="text-left leading-tight">
                <div className="text-[13px] font-semibold tracking-wide">{t.modeCv}</div>
                <div className={`text-[10.5px] font-mono ${mode === "cv" ? "text-black/60" : "text-[var(--muted)]"}`}>
                  {t.modeCvHint}
                </div>
              </div>
            </button>
            <button
              onClick={() => { if (mode !== "cover_letter") { setMode("cover_letter"); setResult(null); } }}
              disabled={loading}
              data-testid="mode-cover-letter"
              role="tab"
              aria-selected={mode === "cover_letter"}
              className={`group flex items-center justify-center gap-3 px-5 py-3 rounded-full transition-all ${
                mode === "cover_letter"
                  ? "bg-[var(--ink)] text-black shadow-[0_0_0_1px_rgba(255,255,255,0.04)]"
                  : "text-[var(--ink-dim)] hover:text-[var(--ink)]"
              }`}
            >
              <Mail size={16} className={mode === "cover_letter" ? "text-black" : ""} />
              <div className="text-left leading-tight">
                <div className="text-[13px] font-semibold tracking-wide">{t.modeCl}</div>
                <div className={`text-[10.5px] font-mono ${mode === "cover_letter" ? "text-black/60" : "text-[var(--muted)]"}`}>
                  {t.modeClHint}
                </div>
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* Main grid */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-20 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">

          {/* Left column */}
          <div className="lg:col-span-5 space-y-5">
            <div className="card p-6">
              <div className="label-eyebrow mb-3">{mode === "cover_letter" ? t.stepUploadCL : t.stepUpload}</div>
              <UploadZone file={file} onFile={setFile} disabled={loading} language={uiLang} />
            </div>

            <div className="card p-6">
              <div className="label-eyebrow mb-3">{t.stepJD}</div>
              <Textarea
                data-testid="jd-input"
                value={jd}
                onChange={(e) => setJD(e.target.value)}
                placeholder={t.placeholderJD}
                className="min-h-[220px] rounded-md bg-[var(--bg-2)] border-[var(--border)] text-[var(--ink)] placeholder:text-[var(--muted)] focus-visible:ring-1 focus-visible:ring-[var(--accent)] text-sm resize-y"
                disabled={loading}
              />
              <div className="mt-2 text-[11px] text-[var(--ink-dim)] font-mono">
                {jd.length} {uiLang === "fr" ? "caractères" : "chars"}
              </div>
            </div>

            <div className="card p-6">
              <div className="label-eyebrow mb-4">{t.stepConfig}</div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-[var(--ink-dim)] mb-1.5">{t.lang}</label>
                  <Select value={outLang} onValueChange={setOutLang} disabled={loading}>
                    <SelectTrigger data-testid="language-select" className="rounded-md bg-[var(--bg-2)] border-[var(--border)] text-[var(--ink)]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fr" data-testid="lang-fr">{t.langFr}</SelectItem>
                      <SelectItem value="en" data-testid="lang-en">{t.langEn}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-xs text-[var(--ink-dim)] mb-1.5">{t.format}</label>
                  <Select value={format} onValueChange={setFormat} disabled={loading}>
                    <SelectTrigger data-testid="format-select" className="rounded-md bg-[var(--bg-2)] border-[var(--border)] text-[var(--ink)]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pdf" data-testid="format-pdf">PDF</SelectItem>
                      <SelectItem value="docx" data-testid="format-docx">DOCX</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <button
              onClick={onOptimize}
              disabled={loading || !file || jd.length < 20}
              className="btn-accent w-full flex items-center justify-center gap-2"
              data-testid="optimize-btn"
            >
              <Sparkles size={16} />
              {loading
                ? (mode === "cover_letter" ? t.analyzingCl : t.analyzing)
                : (mode === "cover_letter" ? t.analyzeCl : t.analyze)}
            </button>
            {loading && <div className="loader-bar w-full" />}
          </div>

          {/* Right column */}
          <div className="lg:col-span-7 space-y-5">
            <Tabs defaultValue="preview" className="w-full" key={mode /* reset selected tab when mode changes */}>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <TabsList className="bg-[var(--surface)] p-1 h-auto gap-1 border border-[var(--border)] rounded-full">
                  <TabsTrigger
                    value="preview"
                    data-testid="tab-preview"
                    className="rounded-full px-4 py-1.5 text-[11px] uppercase tracking-wider font-mono text-[var(--ink-dim)] data-[state=active]:bg-[var(--ink)] data-[state=active]:text-black"
                  >{t.preview}</TabsTrigger>
                  {mode === "cv" ? (
                    <>
                      <TabsTrigger
                        value="analyse"
                        data-testid="tab-analyse"
                        className="rounded-full px-4 py-1.5 text-[11px] uppercase tracking-wider font-mono text-[var(--ink-dim)] data-[state=active]:bg-[var(--ink)] data-[state=active]:text-black"
                      >{t.analyse}</TabsTrigger>
                      <TabsTrigger
                        value="keywords"
                        data-testid="tab-keywords"
                        className="rounded-full px-4 py-1.5 text-[11px] uppercase tracking-wider font-mono text-[var(--ink-dim)] data-[state=active]:bg-[var(--ink)] data-[state=active]:text-black"
                      >{t.keywords}</TabsTrigger>
                    </>
                  ) : (
                    <TabsTrigger
                      value="structure"
                      data-testid="tab-structure"
                      className="rounded-full px-4 py-1.5 text-[11px] uppercase tracking-wider font-mono text-[var(--ink-dim)] data-[state=active]:bg-[var(--ink)] data-[state=active]:text-black"
                    >{t.structure}</TabsTrigger>
                  )}
                </TabsList>
                {(result?.optimized?.target_job_title ||
                  result?.optimized?.header?.title_target) && (
                  <div className="text-right">
                    <div className="label-eyebrow">{t.targetTitle}</div>
                    <div className="text-sm font-medium text-[var(--ink)]">
                      {result.optimized.target_job_title || result.optimized.header?.title_target}
                    </div>
                  </div>
                )}
              </div>

              <TabsContent value="preview" className="mt-4">
                <div className="a4-shell fade-in">
                  <div className="a4-shell-inner">
                    {mode === "cover_letter" ? (
                      <CoverLetterPreview data={result?.optimized} language={outLang} />
                    ) : (
                      <CVPreview data={result?.optimized} language={outLang} />
                    )}
                  </div>
                </div>
              </TabsContent>

              {mode === "cv" && (
                <>
                  <TabsContent value="analyse" className="mt-4">
                    <div className="card p-6 min-h-[500px]">
                      <Rationale data={result?.optimized?.rationale} language={uiLang} />
                    </div>
                  </TabsContent>
                  <TabsContent value="keywords" className="mt-4">
                    <div className="card p-6 min-h-[500px]">
                      <KeywordMatch
                        matched={result?.optimized?.keywords_matched || []}
                        missing={result?.optimized?.keywords_missing || []}
                        language={uiLang}
                      />
                    </div>
                  </TabsContent>
                </>
              )}

              {mode === "cover_letter" && (
                <TabsContent value="structure" className="mt-4">
                  <div className="card p-6 min-h-[500px] space-y-5 fade-in" data-testid="cl-structure">
                    {result?.optimized?.paragraphs ? (
                      <>
                        {[
                          { key: "vous", label: uiLang === "fr" ? "VOUS — l'entreprise" : "YOU — the company" },
                          { key: "moi", label: uiLang === "fr" ? "MOI — le candidat" : "ME — the candidate" },
                          { key: "nous", label: uiLang === "fr" ? "NOUS — la collaboration" : "US — the collaboration" },
                        ].map(({ key, label }) => (
                          <div key={key} className="border-l-2 border-[var(--accent)] pl-4">
                            <div className="label-eyebrow mb-2">{label}</div>
                            <p className="text-sm text-[var(--ink)] leading-relaxed">
                              {result.optimized.paragraphs[key]}
                            </p>
                          </div>
                        ))}
                        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-[var(--border)]">
                          <div className="text-[11px] text-[var(--ink-dim)] font-mono">
                            {t.wordCount}: <span className="text-[var(--ink)]">{result.optimized.word_count || "—"}</span> / 400
                          </div>
                          {Array.isArray(result.optimized.keywords_integrated) && result.optimized.keywords_integrated.length > 0 && (
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-[11px] text-[var(--ink-dim)] font-mono">{t.integratedKw}:</span>
                              {result.optimized.keywords_integrated.map((k, i) => (
                                <span key={i} className="highlight-kw text-xs">{k}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-[var(--ink-dim)] italic">
                        {uiLang === "fr"
                          ? "La structure de la lettre apparaîtra ici après génération."
                          : "The letter structure will appear here after generation."}
                      </p>
                    )}
                  </div>
                </TabsContent>
              )}
            </Tabs>

            {result?.job_id && (
              <a
                href={downloadUrl}
                download={result.filename}
                className="group relative w-full flex items-center justify-center fade-in no-underline overflow-hidden"
                data-testid="download-btn"
                aria-label={`${t.download} — ${result.filename}`}
                title={result.filename}
                style={{
                  background: "linear-gradient(135deg, #ffffff 0%, #e9eaf0 100%)",
                  color: "#000",
                  border: "1px solid #ffffff",
                  borderRadius: 999,
                  padding: "22px 32px",
                  letterSpacing: "0.04em",
                  boxShadow: "0 0 0 1px rgba(255,255,255,0.08), 0 20px 40px -12px rgba(125,211,252,0.25)",
                  transition: "transform 160ms ease, box-shadow 160ms ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
              >
                <Download size={20} className="mr-3" strokeWidth={2.2} />
                <span className="text-xl sm:text-2xl font-semibold tracking-wide">
                  {t.download}
                </span>
              </a>
            )}
          </div>
        </div>

        <footer className="mt-20 pt-6 hairline text-[11px] text-[var(--ink-dim)] font-mono">
          {t.footer}
        </footer>
      </section>
    </div>
  );
}
